import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { Components, Theme } from '@mui/material/styles';

import { DialogTransition } from '@/components/DialogTransition.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';

export const components: Components<Omit<Theme, 'components'>> = {
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f20Regular,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.grey,
        height: 88,
        width: 88,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'medium',
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f16Regular,
        border: '1px solid',
        borderRadius: '8px',
        textTransform: 'none',
        ...(ownerState.size === 'medium' && {
          padding: '4px 12px',
        }),
        ...(ownerState.size === 'small' && {
          padding: '2px 6px',
        }),
        ...(ownerState.variant === 'contained' && {
          '&:focus': {
            background: theme.colors.accentColor,
            borderColor: theme.colors.accentColorDark,
          },
          '&:hover': {
            background: theme.colors.accentColorDark,
          },
          '&.Mui-disabled': {
            background: theme.colors.greyLight,
            borderColor: theme.colors.greyLight,
          },
          background: theme.colors.accentColor,
          borderColor: theme.colors.accentColor,
          color: theme.colors.white,
          ...(ownerState.color === 'info' && {
            '&:hover': {
              backgroundColor: theme.colors.greyDark,
              borderColor: theme.colors.greyDark,
            },
            backgroundColor: theme.colors.grey,
            borderColor: theme.colors.grey,
          }),
          ...(ownerState.color === 'secondary' && {
            '&:hover': {
              backgroundColor: theme.colors.blueDark,
              borderColor: theme.colors.blueDark,
            },
            background: theme.colors.blue,
            borderColor: theme.colors.blue,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'success' && {
            '&:hover': {
              backgroundColor: theme.colors.greenDark,
              borderColor: theme.colors.greenDark,
            },
            background: theme.colors.green,
            borderColor: theme.colors.green,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'warning' && {
            '&:hover': {
              backgroundColor: theme.colors.orangeDark,
              borderColor: theme.colors.orangeDark,
            },
            background: theme.colors.orange,
            borderColor: theme.colors.orange,
            color: theme.colors.white,
          }),
          ...(ownerState.color === 'error' && {
            '&:hover': {
              backgroundColor: theme.colors.redDark,
              borderColor: theme.colors.redDark,
            },
            background: theme.colors.red,
            borderColor: theme.colors.red,
            color: theme.colors.white,
          }),
        }),
        ...(ownerState.variant === 'outlined' && {
          '&:focus': {
            borderColor: theme.colors.accentColorDark,
            color: theme.colors.accentColorDark,
          },
          '&:hover': {
            borderColor: theme.colors.accentColorDark,
            color: theme.colors.accentColorDark,
          },
          '&.Mui-disabled': {
            borderColor: theme.colors.greyLight,
            color: theme.colors.greyLight,
          },
          borderColor: theme.colors.accentColor,
          color: theme.colors.accentColor,
          ...(ownerState.color === 'info' && {
            '&:focus': {
              borderColor: theme.colors.greenDark,
              color: theme.colors.greenDark,
            },
            '&:hover': {
              borderColor: theme.colors.greyDark,
            },
            borderColor: theme.colors.grey,
            color: theme.colors.grey,
          }),
          ...(ownerState.color === 'secondary' && {
            '&:focus': {
              borderColor: theme.colors.blueDark,
              color: theme.colors.blueDark,
            },
            '&:hover': {
              borderColor: theme.colors.blueDark,
            },
            borderColor: theme.colors.blue,
            color: theme.colors.blue,
          }),
          ...(ownerState.color === 'success' && {
            '&:focus': {
              borderColor: theme.colors.greenDark,
              color: theme.colors.greenDark,
            },
            '&:hover': {
              borderColor: theme.colors.greenDark,
            },
            borderColor: theme.colors.green,
            color: theme.colors.green,
          }),
          ...(ownerState.color === 'warning' && {
            '&:focus': {
              borderColor: theme.colors.orangeDark,
              color: theme.colors.orangeDark,
            },
            '&:hover': {
              borderColor: theme.colors.orangeDark,
            },
            borderColor: theme.colors.orange,
            color: theme.colors.orange,
          }),
          ...(ownerState.color === 'error' && {
            '&:focus': {
              borderColor: theme.colors.redDark,
              color: theme.colors.redDark,
            },
            '&:hover': {
              borderColor: theme.colors.redDark,
            },
            borderColor: theme.colors.red,
            color: theme.colors.red,
          }),
        }),
      }),
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiMenuItem-root': {
          '&.MuiMultiSectionDigitalClockSection-item': {
            '&:hover': {
              backgroundColor: theme.colors.accentColorLight,
              color: theme.colors.white,
            },
            '&.Mui-selected': {
              '&:hover': {
                backgroundColor: theme.colors.accentColorDark,
                color: theme.colors.white,
              },
              backgroundColor: theme.colors.accentColor,
              color: theme.colors.white,
            },
          },
          borderRadius: 2,
        },
        '&.MuiPickerDay-root': {
          '&:focus': {
            '&.Mui-selected': {
              backgroundColor: theme.colors.accentColorDark,
              color: theme.colors.white,
            },
          },
          '&:hover': {
            backgroundColor: theme.colors.accentColorLight,
            color: theme.colors.white,
          },
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: theme.colors.accentColorDark,
              color: theme.colors.white,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.colors.accentColorDark,
              color: theme.colors.white,
              opacity: 1,
            },
            backgroundColor: theme.colors.accentColorDark,
            borderRadius: '500px',
            color: theme.colors.white,
          },
        },
        '&.MuiPickerDay-today': {
          border: 'none !important',
          borderBottom: `2px solid ${theme.colors.greyLight} !important`,
          borderRadius: 0,
          outline: 'none !important',
        },
        '&.MuiSwitch-switchBase': {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: themeColors.accentColorDark,
          },
        },
        '&.MuiTab-root': {
          padding: '0',
        },
        '&.MuiTabScrollButton-root': {
          '&.Mui-disabled': {
            color: theme.colors.white,
            opacity: 1,
          },
        },
      }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-checked': {
          color: theme.colors.accentColor,
        },
        paddingBottom: 0,
        paddingTop: 0,
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.color === 'default' && {
          backgroundColor: theme.colors.greenLight,
        }),
        ...(ownerState.color === 'primary' && {
          backgroundColor: theme.colors.greyLight,
        }),
        ...(ownerState.color === 'info' && {
          backgroundColor: theme.colors.purple,
          color: theme.colors.purpleLight,
        }),
      }),
    },
  },
  MuiClockNumber: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiClockNumber-selected': {
          backgroundColor: theme.colors.accentColor,
          color: theme.colors.blueDark,
        },
      }),
    },
  },
  MuiClockPointer: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.colors.accentColorDark,
      }),
      thumb: ({ theme }) => ({
        backgroundColor: theme.colors.accentColor,
        borderColor: theme.colors.accentColorDark,
      }),
    },
  },
  MuiDialog: {
    defaultProps: {
      slots: {
        transition: DialogTransition,
      },
    },
    styleOverrides: {
      paper: {
        '& > div.MuiDialogContent-root': { paddingTop: '8px' },
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
        maxWidth: 960,
        overflow: 'hidden',
        width: 960,
      },
      root: {
        margin: 'auto',
        width: 960,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        paddingBottom: 24,
        paddingRight: 24,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        paddingTop: '48px',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.colors.stroke,
        marginBottom: 16,
        marginTop: 16,
      }),
      vertical: {
        marginBottom: 0,
        marginTop: 0,
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      label: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-focused': {
          color: theme.colors.greyDark,
        },
        color: theme.colors.greyDark,
        top: -4,
        transform: 'none',
      }),
    },
  },
  MuiInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          ...theme.typography.f14Regular,
          color: theme.colors.greyDark,
          opacity: 0.7,
        },
        padding: 0,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&::before': {
          borderBottom: 'none',
        },
        '&:hover:not(.Mui-disabled, .Mui-error)': {
          '&::before': {
            borderBottom: 'none',
          },
        },
        '&:hover:not(.Mui-focused):not(.Mui-error)': {
          borderColor: theme.colors.greyLight,
        },
        '&.Mui-disabled': {
          '&::before': {
            borderBottom: 'none',
          },
        },
        '&.Mui-error': {
          borderColor: theme.colors.red,
        },
        '&.Mui-focused': {
          borderColor: theme.colors.accentColor,
          boxShadow: `0 0 0 3px ${theme.colors.accentColor10}`,
        },
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        boxSizing: 'border-box',
        minHeight: 40,
        padding: '8px 12px',
        transition: `border-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, box-shadow ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&.MuiInput-input': {
          '&.Mui-disabled': {
            textFillColor: theme.colors.grey,
          },
        },
        '&.MuiOutlinedInput-input': {
          '&::placeholder': {
            ...theme.typography.f14Regular,
            color: theme.colors.greyDark,
            opacity: 0.7,
          },
          '&.Mui-disabled': {
            textFillColor: theme.colors.grey,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.accentColor,
          },
          padding: 8,
        },
      }),
      root: ({ theme }) => ({
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            '&:hover': {
              border: 'none',
            },
            border: 'none',
          },
        },
        '&.MuiInput-root': {
          '&.Mui-disabled': {
            backgroundColor: theme.colors.stroke,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.accentColor,
          },
          backgroundColor: theme.colors.white,
          padding: '8px 12px',
        },
        '&.MuiOutlinedInput-root': {
          '&.Mui-disabled': {
            backgroundColor: theme.colors.stroke,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.accentColor,
            boxShadow: `0 0 0 3px ${theme.colors.accentColor10}`,
          },
          minHeight: '40px',
        },
      }),
    },
  },
  MuiInputLabel: {
    defaultProps: {
      // labels are always rendered above the field in this theme (transform: 'none'),
      // shrink keeps them in that state and prevents MUI from hiding the placeholder
      shrink: true,
    },
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-focused': {
          color: theme.colors.greyDark,
        },
        color: theme.colors.greyDark,
        top: -4,
        transform: 'none',
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 4,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&:hover': {
          backgroundColor: theme.colors.stroke20,
        },
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.colors.blueLight,
          },
          backgroundColor: theme.colors.blueLight,
        },
        borderRadius: 2,
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: 'none',
      },
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&:hover:not(.Mui-focused)': {
          borderColor: theme.colors.greyLight,
        },
        '&.Mui-focused': {
          color: theme.colors.black,
        },
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        color: theme.colors.black,
        transform: 'none',
      }),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&:hover': {
          backgroundColor: theme.colors.stroke20,
        },
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.colors.accentColorDark,
          },
          backgroundColor: theme.colors.accentColor,
          color: theme.colors.white,
        },
        borderRadius: 2,
        color: theme.colors.greyDark,
        minWidth: 32,
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        '&.MuiAccordion-root': {
          '&::before': {
            height: 0,
          },
          '&:last-of-type, :first-of-type': {
            borderRadius: 0,
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          borderRadius: 0,
        },
        borderRadius: 0,
      },
    },
  },
  MuiPickersInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&::after': {
          display: 'none',
        },
        '&::before': {
          borderBottom: 'none',
        },
        '&:hover:not(.Mui-disabled):not(.Mui-error)': {
          '&::before': {
            borderBottom: 'none',
          },
          borderColor: theme.colors.greyLight,
        },
        '&.Mui-disabled': {
          '&::before': {
            borderBottom: 'none',
          },
          backgroundColor: theme.colors.stroke,
        },
        '&.Mui-error': {
          borderColor: theme.colors.red,
        },
        '&.Mui-focused': {
          borderColor: theme.colors.accentColor,
          boxShadow: `0 0 0 3px ${theme.colors.accentColor10}`,
        },
        '& .MuiIconButton-root': {
          height: 24,
          padding: 0,
          width: 24,
        },
        '& .MuiInputAdornment-root': {
          height: 24,
          maxHeight: 24,
        },
        backgroundColor: theme.colors.white,
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        boxSizing: 'border-box',
        height: 40,
        maxHeight: 40,
        minHeight: 40,
        padding: '0 12px',
        transition: `border-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, box-shadow ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
      }),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: {
        alignItems: 'center',
        height: '100%',
      },
      sectionsContainer: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '& .MuiPickersPlaceholder-placeholder': {
          color: theme.colors.greyDark,
          opacity: 0.7,
        },
        lineHeight: '22px',
        padding: 0,
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: '8px',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        '&.Mui-checked': {
          color: 'black',
        },
        color: 'black',
      },
    },
  },
  MuiStack: {
    defaultProps: {
      useFlexGap: true,
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: '20px',
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      track: {
        backgroundColor: themeColors.accentColorLight,
      },
    },
  },
  MuiTab: {
    defaultProps: {
      sx: { width: '100%' },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&:hover': { backgroundColor: theme.colors.stroke40 },
        '&.Mui-selected': {
          '&:hover': { backgroundColor: theme.colors.stroke20 },
          color: theme.colors.accentColor,
        },
        backgroundColor: theme.colors.white,
        border: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.greyDark,
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f14Regular,
        '&.MuiTableCell-body': {
          '&:first-of-type': {
            color: theme.colors.accentColor,
          },
        },
        '&.MuiTableCell-head': {
          ...theme.typography.capitalLettersBold,
          backgroundColor: theme.colors.stroke20,
          borderBottom: `1px solid ${theme.colors.greyLight}`,
          color: theme.colors.greyDark,
          fontSize: 11,
          letterSpacing: '0.6px',
          lineHeight: '16px',
          whiteSpace: 'nowrap',
        },
        borderBottom: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.black,
        padding: ownerState?.size === 'small' ? '8px 12px' : '12px 16px',
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-hovered': {
          backgroundColor: theme.colors.stroke20,
        },
        '&.Mui-selected': {
          backgroundColor: theme.colors.blueLight,
        },
        transition: theme.transitions.create('background-color', { duration: 120 }),
      }),
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          color: theme.colors.black,
        },
        '&.Mui-active': {
          '& .MuiTableSortLabel-icon': {
            color: theme.colors.accentColor,
          },
          color: theme.colors.black,
        },
        color: theme.colors.greyDark,
        fontSize: 11,
        letterSpacing: '0.6px',
      }),
    },
  },
  MuiTabList: { defaultProps: { centered: true } },
  MuiTabPanel: {
    styleOverrides: {
      root: {
        padding: '12px',
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: ({ theme }) => ({
        backgroundColor: theme.colors.accentColor,
        left: 0,
      }),
      root: {
        '& .MuiTabs-scrollButtons': {
          backgroundColor: themeColors.white,
        },
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
    },
  },
};
